import { CircleX, Droplets, FileText, Grid2X2, Palette, Printer, Ruler } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getPatternPrintError, getPatternPrintMetrics, openPatternPrintWindow, PRINT_CELL_SIZE_MM, PRINT_MAX_HEIGHT, PRINT_MAX_WIDTH, type PatternPrintMode } from '../core/printPattern';
import type { PatternProject } from '../types';

interface Props {
  project: PatternProject;
  onClose: () => void;
  onPrinted: (mode: PatternPrintMode) => void;
}

export function PrintPatternModal({ project, onClose, onPrinted }: Props) {
  const [mode, setMode] = useState<PatternPrintMode>('color');
  const [error, setError] = useState('');
  const closeRef = useRef<HTMLButtonElement>(null);
  const sizeError = getPatternPrintError(project);
  const metrics = getPatternPrintMetrics(project);

  useEffect(() => { closeRef.current?.focus(); }, []);

  function print() {
    setError('');
    try {
      openPatternPrintWindow(project, mode);
      onPrinted(mode);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '无法打开打印窗口');
    }
  }

  return <div className="modal-backdrop editor-dialog-layer" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="editor-dialog print-dialog" role="dialog" aria-modal="true" aria-labelledby="print-pattern-title">
      <button ref={closeRef} className="editor-dialog-close" type="button" onClick={onClose} aria-label="关闭A4打印设置"><CircleX size={20} /></button>
      <div className="editor-dialog-heading"><span className="editor-dialog-icon print"><Printer size={21} /></span><div><p className="eyebrow">A4 PRINT</p><h2 id="print-pattern-title">A4打印版</h2><p>固定{PRINT_CELL_SIZE_MM}mm格子，以实际大小100%打印；第一版仅支持单张图纸。</p></div></div>

      <div className="print-specs" aria-label="打印规格" aria-live="polite">
        <article><Grid2X2 size={17} /><span><small>图纸格数</small><strong>{project.width} × {project.height}</strong></span></article>
        <article><Ruler size={17} /><span><small>实际尺寸</small><strong>{metrics.widthMm} × {metrics.heightMm}mm</strong></span></article>
        <article><FileText size={17} /><span><small>单页状态</small><strong>{sizeError ? '无法装入' : '1张A4'}</strong></span></article>
      </div>

      {sizeError ? <div className="print-limit-error" role="alert"><strong>当前图纸无法单页打印</strong><span>{sizeError}</span></div> : <div className="print-limit-note"><strong>图纸和完整色卡已装入同一页</strong><span>A4竖向 · 最大 {PRINT_MAX_WIDTH} × {PRINT_MAX_HEIGHT} 格 · 单格 {PRINT_CELL_SIZE_MM}mm · 当前色卡 {metrics.colorCount} 项</span></div>}

      <fieldset className="print-mode-options"><legend>选择打印样式</legend><div>
        <label className={mode === 'color' ? 'active' : ''}>
          <input type="radio" name="print-mode" value="color" checked={mode === 'color'} onChange={() => setMode('color')} />
          <span className="print-mode-icon color"><Palette size={19} /></span><span><strong>彩色打印</strong><small>显示实际色块，深浅色自动切换文字颜色</small></span>
        </label>
        <label className={mode === 'monochrome' ? 'active' : ''}>
          <input type="radio" name="print-mode" value="monochrome" checked={mode === 'monochrome'} onChange={() => setMode('monochrome')} />
          <span className="print-mode-icon mono"><Droplets size={19} /></span><span><strong>单色打印</strong><small>白底黑字，依靠格内色号准确施工</small></span>
        </label>
      </div></fieldset>

      <div className="print-legend-note"><span><i style={{ background: '#f4675c' }} /><i style={{ background: '#f4d738' }} /><i style={{ background: '#3677d2' }} /></span><div><strong>完整紧凑色卡</strong><small>每项只显示色块、色号和数量，并与图纸固定放在同一张A4；放不下时不会缩小或增加第二页。</small></div></div>
      <div className="editor-dialog-note">打印窗口会默认使用A4竖向和零页面边距。请在系统打印设置中选择“实际大小/100%”并关闭页眉页脚；打印机的“无边距模式”无法由网页强制开启。</div>
      {error && <p className="form-error" role="alert">{error}</p>}
      <footer className="editor-dialog-actions"><button className="soft-dialog-button" type="button" onClick={onClose}>取消</button><button className="primary-button compact" type="button" disabled={Boolean(sizeError)} onClick={print}><Printer size={16} />打开打印预览</button></footer>
    </section>
  </div>;
}
